import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface BlurTextProps {
  text: string;
  className?: string;
  animateBy?: 'words' | 'letters';
  direction?: 'top' | 'bottom';
  delay?: number;
  stepDuration?: number;
  threshold?: number;
  rootMargin?: string;
  onAnimationComplete?: () => void;
}

const BlurText: React.FC<BlurTextProps> = ({
  text,
  className = '',
  animateBy = 'words',
  direction = 'top',
  delay = 80,
  stepDuration = 0.4,
  threshold = 0.1,
  rootMargin = '0px',
  onAnimationComplete,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const parts = animateBy === 'words' ? text.split(' ') : text.split('');
  const yStart = direction === 'top' ? -20 : 20;

  return (
    <span ref={ref} className={`inline ${className}`}>
      {parts.map((part, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: yStart, filter: 'blur(8px)' }}
          animate={inView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
          transition={{ duration: stepDuration, delay: i * (delay / 1000), ease: [0.2, 0.65, 0.3, 0.9] }}
          onAnimationComplete={i === parts.length - 1 ? onAnimationComplete : undefined}
          className="inline-block"
          style={{ whiteSpace: animateBy === 'words' ? 'normal' : 'pre' }}
        >
          {part}{animateBy === 'words' && i < parts.length - 1 ? '\u00A0' : ''}
        </motion.span>
      ))}
    </span>
  );
};

export default BlurText;
